import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { TrashIcon } from "@phosphor-icons/react";
import { useTranslation } from "@/features/utils/i18n";
import type { Question } from "@/features/qualifications/types";
import { TYPE_COLORS } from "@/components/molecules/field-editor";

interface QuestionCardProps {
  question: Question;
  index: number;
  onClick: () => void;
  onRemove?: () => void;
  canRemove?: boolean;
}

export const QuestionCard = ({
  question,
  index,
  onClick,
  onRemove,
  canRemove = true,
}: QuestionCardProps) => {
  const { t } = useTranslation("qualifications");
  // Get options for select questions
  const options = question.scoring.values
    ? Object.keys(question.scoring.values)
    : [];

  // Get ranges for number questions
  const ranges = question.scoring.ranges || [];

  const handleCardClick = (e: React.MouseEvent) => {
    // Don't trigger onClick if clicking the remove button
    if ((e.target as HTMLElement).closest("[data-remove-button]")) {
      return;
    }
    onClick();
  };

  return (
    <Card
      className="cursor-pointer p-4 transition-colors hover:border-ring/40 hover:bg-muted/40 md:p-4"
      onClick={handleCardClick}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          {/* Header with type badge and question label */}
          <div className="flex items-center gap-2 mb-2">
            <span className="text-sm text-muted-foreground font-medium">
              Q{index + 1}
            </span>
            <Badge
              variant="secondary"
              className={`${TYPE_COLORS[question.type]} border-0`}
            >
              {t(`forms.editor.types.${question.type}`)}
            </Badge>
          </div>

          {/* Question label */}
          <h4 className="font-medium text-sm mb-1 line-clamp-2">
            {question.label || t("forms.card.untitled")}
          </h4>

          {/* Optional description */}
          {question.description && (
            <p className="text-xs text-muted-foreground mb-2 line-clamp-1">
              {question.description}
            </p>
          )}

          {/* Options/Ranges display */}
          <div className="flex flex-wrap gap-1.5 mt-2">
            {/* Boolean - show scoring values */}
            {question.type === "boolean" && (
              <>
                <Badge variant="outline" className="text-xs">
                  {t("forms.card.yes", {
                    value: question.scoring.values?.["true"] ?? 1,
                  })}
                </Badge>
                <Badge variant="outline" className="text-xs">
                  {t("forms.card.no", {
                    value: question.scoring.values?.["false"] ?? 0,
                  })}
                </Badge>
              </>
            )}

            {/* Select - show options */}
            {question.type === "select" && options.length > 0 && (
              <>
                {options.slice(0, 4).map((option) => (
                  <Badge key={option} variant="outline" className="text-xs">
                    {option}
                  </Badge>
                ))}
                {options.length > 4 && (
                  <Badge variant="outline" className="text-xs">
                    {t("forms.card.more", { count: options.length - 4 })}
                  </Badge>
                )}
              </>
            )}

            {/* Number - show ranges */}
            {question.type === "number" && ranges.length > 0 && (
              <>
                {question.min !== undefined && (
                  <Badge variant="outline" className="text-xs">
                    {t("forms.card.min", { value: question.min })}
                  </Badge>
                )}
                {question.max !== undefined && (
                  <Badge variant="outline" className="text-xs">
                    {t("forms.card.max", { value: question.max })}
                  </Badge>
                )}
                <Badge variant="outline" className="text-xs">
                  {t("forms.card.rules", { count: ranges.length })}
                </Badge>
              </>
            )}

            {/* Text - show scoring logic */}
            {question.type === "text" && question.scoring.scoring_logic && (
              <Badge variant="outline" className="text-xs">
                {t("forms.card.llmScoring")}
              </Badge>
            )}
          </div>
        </div>

        {/* Weight and remove button on the right */}
        <div className="flex flex-col items-end gap-1 shrink-0">
          {question.scoring_mode === "weight" ? (
            <>
              <div className="text-xs text-muted-foreground">
                {t("forms.card.weight")}
              </div>
              <div className="font-semibold text-sm">
                {question.scoring.weight ?? 1}
              </div>
            </>
          ) : (
            <div className="text-xs text-muted-foreground">
              {t(`forms.editor.scoringModes.${question.scoring_mode}`)}
            </div>
          )}
          {onRemove && canRemove && (
            <div data-remove-button className="mt-2">
              <Button
                type="button"
                variant="ghost"
                size="xsicon"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove();
                }}
                confirm={t("forms.editor.removeQuestionConfirm")}
                aria-label={t("forms.editor.removeQuestion")}
                className="text-danger hover:text-danger"
              >
                <TrashIcon className="size-4" />
              </Button>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};
